import { memo } from "react";
import { motion } from "motion/react";
import { Package } from "lucide-react";
import type { ProductCardViewModel } from "../utils/productViewMappers";

type ProductCardV2Props = {
  model: ProductCardViewModel;
  onAdd: () => void;
};

function ProductCardV2Inner({ model, onAdd }: ProductCardV2Props) {
  const badgeClass =
    model.stockBadge === "out"
      ? "bg-destructive text-destructive-foreground"
      : model.stockBadge === "low"
        ? "bg-chart-4/20 text-chart-4"
        : "bg-muted text-muted-foreground";

  return (
    <motion.button
      type="button"
      data-product-id={model.id}
      onClick={() => {
        if (!model.outOfStock) onAdd();
      }}
      disabled={model.outOfStock}
      className="group relative flex h-full w-full flex-col rounded-xl border border-border bg-card p-3 text-left transition-all hover:border-primary hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 [@media(pointer:coarse)]:min-h-[84px] [@media(pointer:coarse)]:text-base"
      whileTap={{ scale: model.outOfStock ? 1 : 0.97 }}
      whileHover={{ y: model.outOfStock ? 0 : -4 }}
    >
      <div className="flex h-full flex-col">
        <div className="mb-2.5 flex aspect-square items-center justify-center overflow-hidden rounded-lg bg-muted">
          <Package className="h-10 w-10 text-muted-foreground" />
        </div>

        <div className="flex-1">
          <h3 className="mb-0.5 line-clamp-2 text-sm font-medium text-foreground sm:text-base">{model.name}</h3>
          {model.unitPriceLabel ? (
            <p className="mb-1.5 text-base font-semibold text-primary sm:text-lg">{model.unitPriceLabel}</p>
          ) : (
            <p className="mb-1.5 text-xs text-muted-foreground">Loading price…</p>
          )}
          {model.mrpLabel ? (
            <p className="mb-1.5 text-[11px] text-muted-foreground line-through">MRP {model.mrpLabel}</p>
          ) : null}
        </div>

        <div className="mt-auto flex items-center justify-between pt-0.5">
          <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium sm:text-xs ${badgeClass}`}>
            {model.stockLabel}
          </span>
          {model.lowStockIndicator ? <span className="text-[11px] font-medium text-chart-4">Low</span> : null}
        </div>
      </div>
    </motion.button>
  );
}

export const ProductCardV2 = memo(ProductCardV2Inner);
